import m from 'mithril/hyperscript';
import { Icon } from '../../components/icon';
import { MaterialIcon } from '../../components/icon/icons';
import { IMithrilStateComponent } from '../../components/mithril.component';
import { Spinner } from '../../components/spinner/spinner';
import { addStyles, DefaultTheme } from '../../components/theme';
import { ColorPalette2022Branding } from '../../components/theme.palette';
import { T, Translate } from '../../i18n/index';
import type { MithrilVNode } from '../../mithril';
import { IkePageName } from '../../page/page';
import { Services } from '../../services';
import { classNames } from '../../util/classnames';
import { CSS as AuthCSS, Style as AuthStyle } from './auth.component.style';
import { MigrateUserController } from './migrate.user.ctrl';

export const CSS = {
	emailInput: 'c-MigrateUser__emailAddress',
	emailInputHasError: 'c-MigrateUser__emailAddress-has-error',
	validationError: 'c-MigrateUser__validationError',
	spinner: 'c-MigrateUser__spinner',
	formError: 'c-MigrateUser__formError',
	successIcon: 'c-MigrateUser__successIcon',
};

const Style = {
	[CSS.emailInput]: {
		borderRadius: DefaultTheme.space(1),
		flexBasis: '100%',
		padding: DefaultTheme.space(3),
		border: `2px solid ${ColorPalette2022Branding.g9}`,
		boxSizing: 'border-box',
		width: '100%',
		fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
	},

	[`${CSS.emailInput}:focus, ${CSS.emailInput}:focus-visible`]: {
		outline: 'none',
		borderColor: ColorPalette2022Branding.lightBlue,
	},

	[CSS.emailInputHasError]: {
		borderColor: `${ColorPalette2022Branding.error} !important`,
		borderBottomRightRadius: '0 !important',
		borderBottomLeftRadius: '0 !important',
	},

	[CSS.validationError]: {
		background: ColorPalette2022Branding.error,
		borderBottomRightRadius: DefaultTheme.space(1),
		borderBottomLeftRadius: DefaultTheme.space(1),
		color: 'white',
		padding: DefaultTheme.space(1),
	},

	[`${CSS.spinner} div`]: {
		border: `2px solid ${ColorPalette2022Branding.g7}`,
		borderTopColor: 'transparent',
	},

	[CSS.formError]: {
		color: ColorPalette2022Branding.error,
		textAlign: 'left',
		marginTop: DefaultTheme.space(2),
	},

	[CSS.successIcon]: {
		color: ColorPalette2022Branding.success,
		fontSize: DefaultTheme.space(8),
		marginBottom: DefaultTheme.space(2),
	},
};

addStyles(AuthStyle);
addStyles(Style);

export class UserSelfMigrate extends IMithrilStateComponent {
	ctrl = new MigrateUserController();

	constructor() {
		super();

		// We still keep the reactions on the state component (instead of the controller) BECAUSE
		// the onRemove hook runs the disposers for any reactions we declare.
		this.reaction(
			() => this.ctrl.value,
			() => {
				this.ctrl.isDirty = true;
				this.ctrl.isValid = MigrateUserController.validateEmail(this.ctrl.value);
			}
		);
	}

	get validationError(): MithrilVNode {
		if (!this.ctrl.isDirty || this.ctrl.isValid) {
			return null;
		}

		return m(
			'div',
			{
				className: CSS.validationError,
			},
			Translate(T.ENTER_VALID_EMAIL)
		);
	}

	get error(): MithrilVNode {
		if (this.ctrl.formError) {
			return m(
				'div',
				{
					className: CSS.formError,
				},
				this.ctrl.formError
			);
		}

		return null;
	}

	get nodes(): MithrilVNode {
		return m('form', { className: AuthCSS.container }, [
			m(
				'div',
				{
					className: AuthCSS.text,
				},
				m(
					'div',
					{
						className: AuthCSS.textInner,
					},
					Translate(T.SSO_UPGRADE_ACCOUNT)
				)
			),

			this.ctrl.hasSubmitted
				? [
						Icon.view(MaterialIcon.CHECK_CIRCLE, {
							className: CSS.successIcon,
						}),
						Translate(this.ctrl.isNewUser ? T.SSO_MIGRATE_EMAIL_SENT : T.SSO_MIGRATE_EMAIL_EXISTING),
					]
				: [
						m('input', {
							className: classNames(CSS.emailInput, {
								[CSS.emailInputHasError]: this.ctrl.hasError,
							}),
							placeholder: 'user@ikegps.com',
							onkeyup: (event: KeyboardEvent) => {
								this.ctrl.value = (event.target as HTMLInputElement).value;
							},
							value: this.ctrl.value,
							disabled: this.ctrl.inFlight,
						}),

						this.validationError,

						this.error,

						m(
							'div',
							{
								className: AuthCSS.padding,
							},
							m(
								'button',
								{
									className: classNames('mdl-button', 'mdl-button--raised', AuthCSS.button),
									onclick: (event: Event) => {
										event.preventDefault();
										// eslint-disable-next-line @typescript-eslint/no-floating-promises
										this.ctrl.submit();
									},
									disabled: !this.ctrl.isDirty || this.ctrl.hasError || this.ctrl.inFlight,
								},
								this.ctrl.inFlight ? Spinner.view(CSS.spinner) : Translate(T.SUBMIT)
							)
						),
					],

			m(
				'div',
				{
					className: AuthCSS.padding,
				},
				m(
					'a',
					{
						className: AuthCSS.link,
						onclick: () => Services.Route.go(IkePageName.login),
					},
					Translate(T.BACK_TO_LOGIN)
				)
			),
		]);
	}
}
